import React, { useEffect, useState } from 'react';
import { autoId, phoneNumberRegex } from '@workday/upc-common';
import { PhoneNumberTextField } from '../../../../shared/phone-number-text-field/phone-number-text-field';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { ID_CANCEL_BUTTON, ID_MOBILE_NUMBER, ID_PHONE_ENTRY_BUTTON } from '../../../../autoIds';
import { useForm } from 'react-hook-form';
import { unmaskPhoneNumber } from '../../../../utils/string';
import { useInitUpdatePhoneMutation } from '../../../../slices/account-api.slice';
import { findCountryCode } from '../../../../utils/countryCodes';
import { useTranslation } from 'react-i18next';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';
import { serverError } from '../../../../utils/server-error';
import { OverflowTooltip } from '@workday/canvas-kit-react/tooltip';

interface ManagePhoneNumbersPhoneEntryProps {
    userInfo: any;
    handleDone: () => void;
    onNext: () => void;
    setPhoneContact: ({ contactId, phone }: { contactId: string; phone: string }) => void;
}

export function ManagePhoneNumbersPhoneEntry({
    onNext,
    handleDone,
    setPhoneContact,
    userInfo
}: ManagePhoneNumbersPhoneEntryProps) {
    const [countryCode, setCountryCode] = useState(() =>
        findCountryCode({ shortName: userInfo.userUnverifiedPhone?.countryCode?.shortName })
    );

    const { t } = useTranslation('account');

    const defaultPhoneNumber = userInfo.userUnverifiedPhone?.phoneNumber || '';
    const { formState, handleSubmit, register, getValues, setError } = useForm({
        reValidateMode: 'onSubmit',
        defaultValues: {
            [ID_MOBILE_NUMBER]: defaultPhoneNumber
        }
    });

    const [initUpdatePhone, initUpdatePhoneMutation] = useInitUpdatePhoneMutation();

    const { ref: phoneNumberInputRef, ...phoneNumberInputProps } = register(ID_MOBILE_NUMBER, {
        minLength: {
            value: 10,
            message: t('ManagePhoneNumbers.phoneEntryNumberTooShort', { ns: 'shared' })
        },
        required: t('Required.fieldGeneric', { ns: 'shared' }),
        validate: value =>
            !!value.match(phoneNumberRegex) || t('ManagePhoneNumbers.phoneEntryNumberValidationError', { ns: 'shared' })
    });

    useErrorHandling(initUpdatePhoneMutation.error, {
        inlineErrors: ['UPC-1006'],
        onInlineError: error => setError(ID_MOBILE_NUMBER, serverError(error))
    });

    let phoneErrorMessage;
    if (initUpdatePhoneMutation.isError) {
        const errorCode = ((initUpdatePhoneMutation.error as any).data as any).errorCode;
        phoneErrorMessage = errorCode != null ? t(errorCode, { ns: 'errors' }) : '';
    } else {
        phoneErrorMessage = formState.errors.mobileNumber?.message;
    }

    useEffect(() => {
        if (initUpdatePhoneMutation.isSuccess) {
            setPhoneContact({ contactId: initUpdatePhoneMutation.data.contactId, phone: getValues().mobileNumber });
            onNext();
        }
    }, [onNext, initUpdatePhoneMutation, setPhoneContact, getValues]);

    function onSubmit(data) {
        const payload = {
            countryCode: countryCode.code,
            number: unmaskPhoneNumber(data[ID_MOBILE_NUMBER])
        };
        initUpdatePhone(payload);
    }

    return (
        <div className='settingsModalContent'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1>{t('ManagePhoneNumbersPhoneEntry.title')}</h1>
                <h2>{t('ManagePhoneNumbersPhoneEntry.description')}</h2>

                <div className='settingsModalContent__body'>
                    <PhoneNumberTextField
                        {...phoneNumberInputProps}
                        countryCode={countryCode}
                        onCountryCodeChange={code => setCountryCode(code)}
                        autoFocus={true}
                        defaultValue={defaultPhoneNumber}
                        errorMessage={phoneErrorMessage}
                        grow={true}
                        id={ID_MOBILE_NUMBER}
                        isDisabled={initUpdatePhoneMutation.isLoading}
                        isInvalid={!!phoneErrorMessage}
                        label={t('ManagePhoneNumbersPhoneEntry.mobileNumberLabel')}
                        countryCodeLabel={t('ManagePhoneNumbersPhoneEntry.countryCodeLabel')}
                        phoneNumberLabel={t('ManagePhoneNumbersPhoneEntry.mobileNumberLabel')}
                        placeholder={t('ManagePhoneNumbersPhoneEntry.addMobileNumberPlaceholder')}
                        ref={phoneNumberInputRef}
                    />

                    <div className='settingsModalContent__footer'>
                        <OverflowTooltip>
                            <PrimaryButton
                                {...autoId(ID_PHONE_ENTRY_BUTTON)}
                                className='settingsModalContent__submit'
                                size='large'
                                grow={true}
                                disabled={initUpdatePhoneMutation.isLoading}
                                type='submit'>
                                {t('ManagePhoneNumbersPhoneEntry.submitButton')}
                            </PrimaryButton>
                        </OverflowTooltip>
                        <div className='settingsModalContent__cancel'>
                            <TertiaryButton
                                {...autoId(ID_CANCEL_BUTTON)}
                                type='button'
                                size='large'
                                onClick={() => handleDone()}>
                                {t('ManagePhoneNumbersPhoneEntry.cancelButton')}
                            </TertiaryButton>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
